/* eslint-disable complexity */
import { Map, fromJS } from 'immutable';

import Actions from 'flow/actions';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { COMPANION_LOCALLY_MUTED } from 'flow/constants/companion-constants';

const initialState = Map({
    isConnected: false,
    isRegistered: false,
    sessions: Map(),
    // What this surface asked the companion to do, until it confirms. Null when nothing is out.
    pendingMute: null,
    pendingMuteCallKey: null,
    // Which press that intent came from, so a stale timeout cannot retire a newer one.
    pendingMuteToken: null,
});

export const phoneReducer = (state = initialState, action) => {
    switch (action && action.type) {
        case Actions.CLEAR_STATE: {
            if (state.get('jssipRetryTimeoutId')) {
                clearTimeout(state.get('jssipRetryTimeoutId'));
            }

            return initialState;
        }
        case Actions.PHONE_ENABLED: {
            return state.set('enabled', action.enabled);
        }
        case Actions.PHONE_NEW_UA: {
            return state.set('userAgent', action.userAgent);
        }
        case Actions.PHONE_NEW_SESSION: {
            return state
                .setIn(
                    ['sessions', action.callId, 'session'],
                    action.sessionObject,
                )
                .setIn(
                    ['sessions', action.callId, 'agentInfo'],
                    fromJS(action.agentInfo),
                )
                .setIn(
                    ['sessions', action.callId, 'isTelavoxTicketNonCall'],
                    action.isTelavoxTicketNonCall,
                )
                .setIn(
                    ['sessions', action.callId, 'telavoxTicketId'],
                    action.telavoxTicketId,
                )
                .setIn(
                    ['sessions', action.callId, 'telavoxTicketData'],
                    action.telavoxTicketData
                        ? fromJS(action.telavoxTicketData)
                        : undefined,
                );
        }
        case Actions.PHONE_REMOVE_SESSION: {
            return state.delete('dtmf').deleteIn(['sessions', action.callId]);
        }
        case Actions.PHONE_SESSION_ACCEPTED: {
            if (!state.hasIn(['sessions', action.callId])) {
                addUserPhoneLogEntry(
                    'Action PHONE_SESSION_ACCEPTED executed with unknown callId',
                );
                return state;
            }
            return state.setIn(
                ['sessions', action.callId, 'isEstablished'],
                true,
            );
        }
        case Actions.PHONE_AUDIO_REFERENCE: {
            return state.set('audioReference', action.reference);
        }
        case Actions.PHONE_RINGTONE_REFERENCE: {
            return state.set('ringtoneAudioReference', action.reference);
        }
        case Actions.PHONE_NEW_DTMF_SENDER: {
            return state.set('dtmf', action.dtmf);
        }
        case Actions.PHONE_MUTED: {
            return state.set('isMuted', action.muted);
        }
        case Actions.PHONE_MUTE_PENDING: {
            return state
                .set('pendingMute', action.muted)
                .set('pendingMuteToken', action.token ?? null)
                .set('pendingMuteCallKey', action.callKey ?? null);
        }
        /**
         * A tagged clear is one press's own timeout and only retires that press. An untagged one
         * is unconditional - the sweep giving up on a call that has gone.
         *
         * Without the tag a user muting and unmuting in quick succession loses the second intent
         * to the first press's timeout, and the button flips back to whatever the call still says.
         */
        case Actions.PHONE_MUTE_PENDING_CLEARED: {
            if (
                action.token !== undefined &&
                action.token !== state.get('pendingMuteToken')
            ) {
                return state;
            }
            return state
                .set('pendingMute', null)
                .set('pendingMuteToken', null)
                .set('pendingMuteCallKey', null);
        }
        // Only a report that agrees, about the call the intent was for, ends the wait. Reports
        // arrive for every call this client can see - other calls of the user's and colleagues'
        // alike - and the mic button reads the focused call alone.
        case Actions.EXTENSION_UPDATE_ACTIVE_CALL_BAG_VARIABLE: {
            if (
                action.variableName !== COMPANION_LOCALLY_MUTED ||
                state.get('pendingMute') === null ||
                action.callKey !== state.get('pendingMuteCallKey')
            ) {
                return state;
            }

            // A string on the wire, and tryParseJsonObject leaves non-objects alone, so this is
            // "true"/"false". Boolean arm as in selectCompanionLocallyMuted: a guard, not the
            // case that happens.
            const reported =
                action.variableValue === true ||
                action.variableValue === 'true';

            return reported === state.get('pendingMute')
                ? state
                      .set('pendingMute', null)
                      .set('pendingMuteToken', null)
                      .set('pendingMuteCallKey', null)
                : state;
        }
        case Actions.PHONE_ISONHOLD: {
            if (!state.hasIn(['sessions', action.callId])) {
                addUserPhoneLogEntry(
                    'Action PHONE_ISONHOLD executed with unknown callId',
                );
                return state;
            }
            return state.setIn(
                ['sessions', action.callId, 'isOnHold'],
                action.isOnHold,
            );
        }
        case Actions.PHONE_CONNECTED: {
            return state.set('isConnected', action.connected);
        }
        case Actions.PHONE_REGISTERED: {
            return state.set('isRegistered', action.registered);
        }
        case Actions.PHONE_PLAY_RINGTONE_SETTING: {
            return state.set('playRingtoneSetting', action.playRingtoneSetting);
        }
        case Actions.FINISH_UNPARKING: {
            return state.delete('unparking');
        }
        case Actions.SIP_RETRY_TIMEOUT_ID: {
            clearTimeout(state.get('jssipRetryTimeoutId'));
            return state.set('jssipRetryTimeoutId', action.timeoutId);
        }
        case Actions.SET_CALL_WAITING: {
            return state.set('callWaiting', action.callWaiting);
        }
        case Actions.SET_PAI_HEADER: {
            return state.set('pai', action.pai);
        }
        case Actions.REMOVE_PAI_HEADER: {
            return state.delete('pai');
        }
        default: {
            return state;
        }
    }
};

export default phoneReducer;
